import { Header } from "@/components/Header";
import { Footer } from "@/components/ui/footer";
import { BookOpen } from "lucide-react";

export function TermsPage() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      <main className="flex-1 max-w-3xl mx-auto w-full px-8 py-12">
        <h1 className="text-4xl font-bold mb-8">Terms of Service</h1>
        <p className="text-muted-foreground mb-8">
          Last updated:{" "}
          {new Date().toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          })}
        </p>

        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-4">
              1. Acceptance of Terms
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              By accessing or using Scholar Sphere, you agree to be bound by
              these Terms of Service. If you do not agree to these terms, please
              do not use our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">
              2. Description of Service
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Scholar Sphere provides an interactive learning platform for
              standardized test preparation, including practice questions,
              progress tracking, and educational resources for SAT, ACT, and AP
              examinations.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">3. Age Requirement (COPPA)</h2>
            <p className="text-muted-foreground leading-relaxed">
              Scholar Sphere is intended for users who are 13 years of age or older. By creating an account, you represent and warrant that you are at least 13 years old. If you are under 13, you may not create an account or use our services.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              In compliance with the Children's Online Privacy Protection Act (COPPA), we do not knowingly collect personal information from children under the age of 13. If we become aware that a user is under 13, we will promptly delete their account and any associated data. If you believe a child under 13 has provided us with personal information, please contact us at{" "}
              <a href="mailto:legal@scholarsphere.com" className="text-primary hover:underline">legal@scholarsphere.com</a>{" "}so we can take appropriate action.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">4. User Accounts</h2>
            <p className="text-muted-foreground leading-relaxed">
              To access certain features, you must create an account. You are
              responsible for:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 mt-4">
              <li>Providing accurate and complete information</li>
              <li>Maintaining the security of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized use</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">5. Acceptable Use</h2>
            <p className="text-muted-foreground leading-relaxed">
              You agree not to:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 mt-4">
              <li>Share your account with others</li>
              <li>Copy, distribute, or sell our content</li>
              <li>Use automated systems to access our platform</li>
              <li>Attempt to circumvent any security measures</li>
              <li>Use the service for any illegal purpose</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">
              6. Intellectual Property
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              All content on Scholar Sphere, including questions, explanations,
              graphics, and software, is owned by Scholar Sphere or its
              licensors and is protected by copyright and other intellectual
              property laws. SAT® is a trademark of the College Board, ACT® is a
              trademark of ACT, Inc., and AP® is a trademark of the College
              Board, which were not involved in the production of this product.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">
              7. Subscriptions and Payments
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Some features require a paid subscription. By subscribing, you
              agree to:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 mt-4">
              <li>Pay the applicable subscription fees</li>
              <li>Automatic renewal unless you cancel</li>
              <li>No refunds for partial subscription periods</li>
              <li>Price changes with reasonable notice</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">8. Disclaimer</h2>
            <p className="text-muted-foreground leading-relaxed">
              Scholar Sphere is provided "as is" without warranties of any kind.
              We do not guarantee any specific test scores or results. Your
              success depends on your individual effort and study habits.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">
              9. Limitation of Liability
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Scholar Sphere shall not be liable for any indirect, incidental,
              special, consequential, or punitive damages arising from your use
              of the service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">10. Termination</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may terminate or suspend your account at any time for
              violations of these terms. You may also delete your account at any
              time through your account settings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">
              11. Changes to Terms
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update these terms from time to time. We will notify you of
              significant changes by posting a notice on our platform or sending
              you an email.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">12. Contact</h2>
            <p className="text-muted-foreground leading-relaxed">
              For questions about these Terms, please contact us at{" "}
              <a
                href="mailto:legal@scholarsphere.com"
                className="text-primary hover:underline"
              >
                legal@scholarsphere.com
              </a>
            </p>
          </section>
        </div>
      </main>

      <div className="mx-auto px-8 w-full max-w-5xl">
        <Footer
          logo={
            <div className="w-10 h-10 rounded-full flex items-center justify-center">
              <BookOpen className="size-5" />
            </div>
          }
          brandName="Scholar Sphere"
          socialLinks={[]}
          mainLinks={[
            { href: "/", label: "Home" },
            { href: "/subjects", label: "Question Banks" },
            { href: "/dashboard", label: "Dashboard" },
          ]}
          legalLinks={[
            { href: "/privacy", label: "Privacy" },
            { href: "/terms", label: "Terms" },
          ]}
          copyright={{
            text: `© ${new Date().getFullYear()} Scholar Sphere`,
            license: "All rights reserved",
          }}
        />
      </div>
    </div>
  );
}
